<script lang="ts">
    import { onMount } from 'svelte/internal'
    import dayjs from 'dayjs'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import type { FormV2FormSignatureLog } from '../../../modules/toolsApi/forms-v2/form-v2-signature-logs/form-v2-form-signature-log'
    import type { FormV2FormSignature } from '../../../modules/toolsApi/forms-v2/form-v2-form-signatures/form-v2-form-signature'
    import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'

    export let formV2FormId: string

    const MODE_LINK = 1
    const MODE_EMAIL = 2

    let show = false
    let logs: FormV2FormSignatureLog[] = []
    let grids: GridClass[] = []
    let documentGrid: GridClass | null = null

    onMount(load)

    async function load() {
        try {
            show = true
            const [form, logsResult, signersResult] = await Promise.all([
                window.api.tools.formsV2.forms.getOne(formV2FormId),
                window.api.tools.formsV2.formSignatureLogs.getAll({ formV2FormId }),
                window.api.tools.formsV2.formSignatures.getAll({ formV2FormId }),
            ])
            logs = logsResult
            documentGrid = buildDocumentGrid(form, signersResult)
            grids = logs.map((log) => buildGrid(log, signersResult))
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function signatureModeLabel(mode: number | null | undefined) {
        if (mode === MODE_LINK) return 'Link de assinatura (assinatura simples)'
        if (mode === MODE_EMAIL) return 'Assinatura por e-mail (assinatura avançada)'
        return '—'
    }

    function buildDocumentGrid(form: FormV2Form, signers: FormV2FormSignature[]) {
        const grid = new GridClass()
        grid.title = 'Documento'
        grid.showHeaders = true
        grid.showActs = false
        grid.showFilter = false
        grid.fontSize = '13px'
        grid.cols.push(new GridCol({ content: 'Campo', type: GridColTypeEnum.string, size: 3 }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.string }))

        const rows: [string, string][] = [
            ['Título', form.title || '—'],
            ['ID do documento', form.formV2FormId],
            ['Modalidade de assinatura', signatureModeLabel(form.signatureMode)],
            ['Quantidade de assinantes', String(signers.length)],
            ['Quantidade de páginas', form.pageCount != null ? String(form.pageCount) : '—'],
            ['Assinado em', form.signedAt ? dayjs(form.signedAt).format('DD/MM/YYYY HH:mm:ss') : '—'],
        ]
        for (const [field, value] of rows) {
            const line = new GridLine()
            line.slots.push({ content: field })
            line.slots.push({ content: value })
            grid.lines.push(line)
        }
        return grid
    }

    function formatGeolocation(geo: { latitude: number; longitude: number; accuracy: number } | null | undefined) {
        if (!geo) return 'Não informada (negada ou indisponível)'
        return geo.latitude + ', ' + geo.longitude + ' (precisão: ' + Math.round(geo.accuracy) + 'm)'
    }

    function buildMapsUrl(geo: { latitude: number; longitude: number }) {
        return 'https://www.google.com/maps/search/?api=1&query=' + geo.latitude + ',' + geo.longitude
    }

    function signatureMethodLabel(method: string | null | undefined) {
        if (method === 'a1') return 'Certificado A1 (e-CNPJ ICP-Brasil)'
        if (method === 'self-signed') return 'Avançada (chave da plataforma)'
        return method || '—'
    }

    function buildGrid(log: FormV2FormSignatureLog, signers: FormV2FormSignature[]) {
        const signer = signers.find((s) => s.formV2FormSignatureId === log.formV2FormSignatureId)

        const grid = new GridClass()
        grid.title = log.personName
        grid.showHeaders = true
        grid.showActs = false
        grid.showFilter = false
        grid.fontSize = '13px'
        grid.cols.push(new GridCol({ content: 'Campo', type: GridColTypeEnum.string, size: 2 }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        const geo = log.metadata?.geolocation
        const rows: [string, string, GridButton[]][] = [
            ['Nome', log.personName, []],
            ['Documento', log.personDocument, []],
            ['Telefone', log.personPhone || '—', []],
            ['E-mail', log.personEmail || '—', []],
            ['Endereço', log.personAddress || '—', []],
            ['ID único', signer?.friendlySignatureKey || '—', []],
            ['Método de assinatura', signatureMethodLabel(log.signatureMethod), []],
            ['IP', log.metadata?.requestIP || '—', []],
            ['User-Agent', log.metadata?.userAgent || '—', []],
            [
                'Geolocalização',
                formatGeolocation(geo),
                geo ? [new GridButton({ label: 'Abrir', callback: () => window.open(buildMapsUrl(geo), '_blank') })] : [],
            ],
        ]
        // Só entram se tiverem valor — e nessa ordem, "Assinado em" sempre por último.
        if (signer?.emailSentAt) {
            rows.push(['E-mail enviado em', dayjs(signer.emailSentAt).format('DD/MM/YYYY HH:mm:ss'), []])
        }
        if (signer?.viewedAt) {
            rows.push(['Visualizado em', dayjs(signer.viewedAt).format('DD/MM/YYYY HH:mm:ss'), []])
        }
        if (log.createdAt) {
            rows.push(['Assinado em', dayjs(log.createdAt).format('DD/MM/YYYY HH:mm:ss'), []])
        }
        for (const [field, value, buttons] of rows) {
            const line = new GridLine()
            line.slots.push({ content: field })
            line.slots.push({ content: value })
            line.slots.push({ content: buttons })
            grid.lines.push(line)
        }
        return grid
    }
</script>

<div class="root">
    {#if documentGrid}
        <Zgrid grid={documentGrid} class="m-3" />
    {/if}
    {#each logs as log, index (log.formV2FormSignatureLogId)}
        <Zgrid grid={grids[index]} class="m-3" />
    {/each}
    {#if logs.length === 0 && !show}
        <div class="text-muted m-3">Nenhum registro de assinatura ainda.</div>
    {/if}
    <LoadSpinner {show} />
</div>

<style>
    .root {
        position: relative;
        min-height: 150px;
    }
</style>
